import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable as LexicalContentEditable } from "@lexical/react/LexicalContentEditable";
import { EditorRefPlugin } from "@lexical/react/LexicalEditorRefPlugin";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { TableOfContentsEntry, TableOfContentsPlugin } from "@lexical/react/LexicalTableOfContentsPlugin";
import { HeadingNode } from "@lexical/rich-text";
import { styled, SxProps, TypographyProps } from "@mui/material";
import { $getRoot, $insertNodes, LexicalEditor } from "lexical";
import { useEffect, useRef } from "react";
import { NotifyOnUpdatePlugin } from "./plugins/NotifyOnUpdatePlugin";
import { htmlToNodes, TextEditorContainer, TextEditorContentContainer, TextEditorPlaceholder } from "./TextEditor";
import { theme } from "./theme";

const TextViewerContainer = styled(TextEditorContainer)`
  border: none;
  padding: 0;
  opacity: 1;
`;

const TextViewerContentContainer = styled(TextEditorContentContainer)`
  min-height: 20px;
`;

const TextViewerContentEditable = styled(LexicalContentEditable)`
  outline: 0px solid transparent;
`;

const TextViewerPlaceholder = styled(TextEditorPlaceholder)`
  top: 0;
  left: 0;
`;

interface TextViewerProps {
  value: string;
  placeholder?: string;
  renderTableOfContents?: (tableOfContentsArray: TableOfContentsEntry[]) => JSX.Element;
  sx?: SxProps;
  onContentUpdated?: () => void;
  typographyVariant?: "body1" | "caption";
}

const TextViewer = (props: TextViewerProps) => {
  const editor = useRef<LexicalEditor>(null);
  const typographyVariant: TypographyProps["variant"] = props.typographyVariant ?? "body1";

  useEffect(() => {
    if (editor.current == null) {
      return;
    }

    editor.current.update(() => {
      const root = $getRoot();
      root.clear();
      root.append(...htmlToNodes(editor.current!, props.value));
    });
  }, [props.value]);

  return (
    <TextViewerContainer $readonly sx={{ scrollMarginTop: 130 }}>
      <LexicalComposer
        initialConfig={{
          namespace: "TextViewer",
          onError: (e) => console.error(e),
          theme: theme,
          editable: false,
          editorState: (editor) => {
            editor.update(() => {
              $insertNodes(htmlToNodes(editor, props.value));
            });
          },
          nodes: [AutoLinkNode, LinkNode, ListNode, ListItemNode, HeadingNode],
        }}
      >
        <TextViewerContentContainer sx={props.sx}>
          <EditorRefPlugin editorRef={editor} />
          <NotifyOnUpdatePlugin onUpdate={props.onContentUpdated} />
          <RichTextPlugin
            contentEditable={<TextViewerContentEditable sx={(theme) => theme.typography[typographyVariant]} />}
            placeholder={
              props.placeholder ? (
                <TextViewerPlaceholder variant={typographyVariant}>{props.placeholder}</TextViewerPlaceholder>
              ) : null
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <TableOfContentsPlugin>
            {(tableOfContentsArray) => props.renderTableOfContents?.(tableOfContentsArray) ?? <></>}
          </TableOfContentsPlugin>
        </TextViewerContentContainer>
      </LexicalComposer>
    </TextViewerContainer>
  );
};

export { TextViewer };
